import { Dictionary } from "@/utils/Dictionary";
import instance from "../axiosConfig";
import { Announcement, Discipline, FooterLink, Locale, Phase, Software, UseCase } from "./models";
import { LocaleResponse } from "./models/LocaleResponse";

export class LocaleService {
    private regionCode: string;
    private languageCode: string;

    constructor(regionCode: string, languageCode: string) {
        this.regionCode = regionCode;
        this.languageCode = `${regionCode}-${languageCode}`;
    }

    static get = async (isPreview: boolean = false): Promise<Array<Locale>> => {
        const version = isPreview ? "wip" : "";
        const response = await instance.get<Array<LocaleResponse>>(`api/regions?version=${version}`);
        const result: Locale[] = response.data.map((locale) => ({
            ...locale,
            labels: locale.languages.reduce((acc, language) => {
                const key = language.slug.split("-")[1];
                acc[key] = language.regionLabel;

                return acc;
            }, {} as Dictionary<string>),
        }));

        return result.sort((a, b) => a.regionCode.localeCompare(b.regionCode));
    };

    getSoftware = async (version: string = ""): Promise<Array<Software>> => {
        const response = await instance.get<Array<Software>>(
            `api/Regions/${this.regionCode}/Software?version=${version}`
        );

        return response.data;
    };

    getDisciplines = async (version: string = ""): Promise<Array<Discipline>> => {
        const response = await instance.get<Array<Discipline>>(
            `api/Regions/${this.regionCode}/Languages/${this.languageCode}/Disciplines?version=${version}`
        );

        const promises = response.data.map((discipline) => {
            if (discipline.icon && discipline.icon.endsWith(".svg")) {
                return instance.get<string>(discipline.icon).then((svgResponse) => {
                    discipline.svgIcon = svgResponse.data;
                });
            }

            return Promise.resolve();
        });

        await Promise.all(promises);

        return response.data;
    };

    getPhases = async (version: string = ""): Promise<Array<Phase>> => {
        const response = await instance.get<Array<Phase>>(`api/Regions/${this.regionCode}/Phases?version=${version}`);

        return response.data;
    };

    getUseCases = async (version: string = ""): Promise<Array<UseCase>> => {
        const response = await instance.get<Array<UseCase>>(
            `api/Regions/${this.regionCode}/Languages/${this.languageCode}/UseCases?version=${version}`
        );

        if (response.data.some((x) => x.attachedDiscipline !== undefined)) {
            response.data.forEach((uc) => {
                uc.attachedDiscipline = uc.attachedDiscipline ?? [];
            });
        }

        return response.data;
    };

    getNotification = async (): Promise<Announcement> => {
        const response = await instance.get<Announcement>(
            `/api/Regions/${this.regionCode}/Languages/${this.languageCode}/Notification`
        );

        return response.data;
    };

    getFooterLinks = async (): Promise<Array<FooterLink>> => {
        const response = await instance.get<Array<FooterLink>>(
            `/api/Regions/${this.regionCode}/Languages/${this.languageCode}/FooterLinks`
        );

        return response.data;
    };
}
